import { useEffect, useId, useRef, useState } from 'react'
import { delta } from '../lib/format'

/* ------------------------------------------------------------------ layout */

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  useEffect(() => { if (typeof title === 'string') document.title = `${title} | Popup Motion` }, [title])
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div className="min-w-0">
        {eyebrow && <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-red mb-1">{eyebrow}</div>}
        <h1 className="text-3xl md:text-4xl text-ink leading-none">{title}</h1>
        {subtitle && <p className="text-ink-3 mt-2 text-sm max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

export function Page({ children, wide = false }) {
  return <div className={'p-5 md:p-8 min-w-0 ' + (wide ? 'max-w-[1400px]' : 'max-w-6xl')}>{children}</div>
}

/* ---------------------------------------------------------------- metrics */

/**
 * One-line definitions for every metric the app shows. Use them as the `title`
 * on tiles and table headers so nobody has to guess what a number means.
 */
export const STAT_DEFS = {
  visitors: 'Unique visitors who loaded a page with the snippet installed',
  eligible: 'Visitors who passed a popup\'s display rules, before any trigger fired',
  views: 'Times a popup was displayed (one per impression)',
  engaged: 'Impressions with at least one click or focus inside the popup',
  started: 'Impressions where a form field was focused',
  submitted: 'Forms sent from the popup',
  accepted: 'Submissions Klaviyo accepted (valid email or phone, not suppressed)',
  netNew: 'Profiles that did not exist in Klaviyo before this signup',
  verified: 'Signups Klaviyo confirmed as new profiles, not just form submits',
  consent: 'Net-new subscribers who confirmed consent (double opt-in)',
  flow: 'Net-new subscribers who entered the welcome flow',
  customers: 'Subscribers who placed their first order inside the attribution window',
  conversionRate: 'Submissions ÷ views',
  signupRate: 'Net-new subscribers ÷ views',
  revenue: 'Klaviyo Placed Order revenue from attributed subscribers',
  rpm: 'Attributed revenue per 1,000 popup views',
  subscriberValue: 'Attributable revenue ÷ net-new subscribers',
  lift: 'Difference in purchase rate between visitors who saw the popup and the holdout group',
  objective: 'What the A/B test is optimising for',
  offer: 'The incentive this popup gives, used to compare cohorts',
  holdout: 'Share of eligible visitors who never see the popup, to measure true lift',
}

/** KPI tile with optional previous-period comparison. `invert` = lower is better. */
export function StatCard({ label, value, cur, prev, hint, invert = false, accent = false, definition }) {
  const d = prev !== undefined ? delta(cur ?? 0, prev ?? 0) : null
  const good = d && d.dir !== 'flat' ? (d.dir === 'up') !== invert : null
  return (
    <div className={'card min-w-0 ' + (accent ? 'border-red/40 bg-red-tint/40' : '')} title={definition}>
      <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-ink-3 flex items-center gap-1">{label}{definition && <InfoTip text={definition} />}</div>
      <div className="font-heading font-bold text-3xl text-ink mt-1 leading-none tabular-nums">{value}</div>
      {/* Wraps rather than truncates: these cards sit six-across, and a clipped
          "Klaviyo-conf…" tells the reader nothing. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-2 min-h-[18px]">
        {d && (
          <span className={'text-xs font-semibold whitespace-nowrap ' + (good === null ? 'text-ink-3' : good ? 'text-status-approvedTxt' : 'text-status-deniedTxt')}>
            {d.dir === 'up' ? '▲' : d.dir === 'down' ? '▼' : '•'} {d.text}
          </span>
        )}
        {hint && <span className="text-xs text-ink-3 leading-snug">{hint}</span>}
      </div>
    </div>
  )
}

/** Compact inline stat: label above value, definition on hover. */
export function Stat({ label, value, definition, className = '' }) {
  return (
    <div className={'min-w-0 ' + className} title={definition}>
      <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-ink-3 truncate">{label}</div>
      <div className="font-heading font-bold text-xl text-ink leading-tight tabular-nums">{value}</div>
    </div>
  )
}

export const RANGES = [
  { key: '7', label: '7 days' }, { key: '30', label: '30 days' }, { key: '90', label: '90 days' }, { key: '365', label: '12 months' },
]
export function RangePicker({ value, onChange }) {
  return <SegmentedControl options={RANGES.map((r) => ({ value: r.key, label: r.label }))} value={value} onChange={onChange} ariaLabel="Date range" />
}

/* --------------------------------------------------------------- controls */

/** A row of mutually exclusive options (replaces a select when there are 2 to 5 short choices). */
export function SegmentedControl({ options, value, onChange, ariaLabel, size = 'sm', className = '' }) {
  const pad = size === 'xs' ? 'px-2.5 py-1 text-[11px]' : size === 'md' ? 'px-4 py-2.5 text-sm' : 'px-3 py-2 text-xs'
  return (
    <div className={'inline-flex rounded border border-border bg-white overflow-hidden max-w-full ' + className} role="group" aria-label={ariaLabel}>
      {options.map((o) => {
        const opt = typeof o === 'string' ? { value: o, label: o } : o
        const on = value === opt.value
        return (
          <button key={opt.value} type="button" onClick={() => onChange(opt.value)} aria-pressed={on} title={opt.title} disabled={opt.disabled}
            className={'font-semibold whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ink-2 disabled:opacity-40 ' + pad + ' ' + (on ? 'bg-ink text-white' : 'text-ink-2 hover:bg-surface')}>
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

/** A small ⓘ that reveals a definition on hover or keyboard focus. */
export function InfoTip({ text, label = 'What does this mean?' }) {
  const id = useId()
  if (!text) return null
  return (
    <span className="relative inline-flex group align-middle">
      <button type="button" aria-label={label} aria-describedby={id}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-border-2 text-[10px] leading-none text-ink-3 hover:text-ink hover:border-ink-3 focus:outline-none focus-visible:ring-1 focus-visible:ring-ink-2 normal-case tracking-normal font-semibold">
        i
      </button>
      {/* Must be display:none when idle, not just transparent — an always-laid-out
          224px box anchored to the icon widens the whole page wherever a tip sits
          near the right edge. Capped to the viewport on narrow screens. */}
      <span id={id} role="tooltip"
        className="pointer-events-none hidden group-hover:block group-focus-within:block absolute left-1/2 top-full z-30 mt-1.5 w-56 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded bg-ink px-3 py-2 text-left text-[11px] font-normal normal-case tracking-normal leading-snug text-white shadow-lg">
        {text}
      </span>
    </span>
  )
}

/**
 * Step indicator for wizards. `steps` = [{key,label,hint?}], `current` = index.
 * Steps before `current` (or listed in `done`) are complete and clickable.
 * Scrolls horizontally with fade edges on small screens.
 */
export function Stepper({ steps, current, done, onSelect, className = '' }) {
  const isDone = (i, s) => (done ? (typeof done === 'function' ? done(s, i) : done.includes?.(s.key) || done.has?.(s.key)) : i < current)
  return (
    <div className={'relative ' + className}>
      <ol className="flex items-center gap-1 overflow-x-auto pb-1 scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Steps">
        {steps.map((s, i) => {
          const state = i === current ? 'current' : isDone(i, s) ? 'done' : 'next'
          const clickable = onSelect && (state === 'done' || state === 'current')
          return (
            <li key={s.key} className="flex items-center gap-1 flex-none">
              <button type="button" disabled={!clickable} onClick={() => clickable && onSelect(s.key, i)} aria-current={state === 'current' ? 'step' : undefined}
                className={'flex items-center gap-2 whitespace-nowrap rounded-full border pl-1.5 pr-3 py-1 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ink-2 ' +
                  (state === 'current' ? 'bg-ink text-white border-ink' : state === 'done' ? 'bg-white text-ink border-border hover:border-ink-4' : 'bg-white text-ink-4 border-border cursor-default')}>
                <span className={'inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] ' +
                  (state === 'current' ? 'bg-red text-white' : state === 'done' ? 'bg-status-approvedBg text-status-approvedTxt' : 'bg-surface text-ink-4')}>
                  {state === 'done' ? '✓' : i + 1}
                </span>
                {s.label}
              </button>
              {i < steps.length - 1 && <span className={'h-px w-3 ' + (isDone(i, s) ? 'bg-status-approvedTxt/40' : 'bg-border')} aria-hidden />}
            </li>
          )
        })}
      </ol>
      <span className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-surface to-transparent md:hidden" aria-hidden />
      <span className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-surface to-transparent md:hidden" aria-hidden />
    </div>
  )
}

/* -------------------------------------------------------------- feedback */

/** Loading placeholder. `lines` for text, or pass `className` for a custom shape. */
export function Skeleton({ lines = 0, className = '' }) {
  if (lines > 0) {
    return (
      <div className={'space-y-2 ' + className} aria-hidden>
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} className="h-3 rounded bg-border/70 animate-pulse" style={{ width: `${[92, 70, 84, 60, 78][i % 5]}%` }} />
        ))}
      </div>
    )
  }
  return <div className={'rounded bg-border/70 animate-pulse ' + className} aria-hidden />
}
export function SkeletonCards({ n = 4, className = '' }) {
  return (
    <div className={'grid gap-4 ' + (className || 'sm:grid-cols-2 lg:grid-cols-4')} aria-busy="true" aria-label="Loading">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="card space-y-3"><Skeleton className="h-3 w-24" /><Skeleton className="h-8 w-32" /><Skeleton className="h-3 w-16" /></div>
      ))}
    </div>
  )
}
export function SkeletonTable({ rows = 6, cols = 5 }) {
  return (
    <div className="card p-0 overflow-hidden" aria-busy="true" aria-label="Loading">
      <div className="bg-surface px-4 py-3 flex gap-6">{Array.from({ length: cols }).map((_, i) => <Skeleton key={i} className="h-3 w-20" />)}</div>
      <div className="divide-y divide-border">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="px-4 py-3.5 flex gap-6 items-center">{Array.from({ length: cols }).map((_, c) => <Skeleton key={c} className={'h-3 ' + (c === 0 ? 'w-40' : 'w-16')} />)}</div>
        ))}
      </div>
    </div>
  )
}

/** Empty state with an optional illustration slot (an SVG, emoji or icon). */
export function EmptyState({ title, body, action, illustration, compact = false }) {
  return (
    <div className={'card text-center ' + (compact ? 'py-8' : 'py-12')}>
      {illustration && <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-surface text-ink-3 text-2xl" aria-hidden>{illustration}</div>}
      <div className="font-heading uppercase font-bold text-xl text-ink">{title}</div>
      {body && <p className="text-sm text-ink-3 mt-2 max-w-md mx-auto">{body}</p>}
      {action && <div className="mt-5 flex justify-center gap-2">{action}</div>}
    </div>
  )
}
export function Empty(props) { return <EmptyState {...props} /> }

/** Inline error state for a data view, with a retry. */
export function ErrorState({ title = 'Could not load this', body, onRetry }) {
  return (
    <div className="card border-status-deniedTxt/20 bg-status-deniedBg/40 text-center py-8">
      <div className="font-heading uppercase font-bold text-lg text-status-deniedTxt">{title}</div>
      {body && <p className="text-sm text-ink-2 mt-1 max-w-md mx-auto">{body}</p>}
      {onRetry && <button type="button" className="btn-ghost mt-4 px-4 py-2 text-sm" onClick={onRetry}>Try again</button>}
    </div>
  )
}

/** Slim banner. tone: info | warn | fail | ok */
export function Banner({ tone = 'info', children, action, className = '' }) {
  const cls = {
    info: 'bg-status-sentBg text-status-sentTxt border-status-sentTxt/20',
    warn: 'bg-status-pendingBg text-status-pendingTxt border-status-pendingTxt/20',
    fail: 'bg-status-deniedBg text-status-deniedTxt border-status-deniedTxt/20',
    ok: 'bg-status-approvedBg text-status-approvedTxt border-status-approvedTxt/20',
  }[tone]
  return (
    <div className={'flex flex-wrap items-center gap-x-3 gap-y-1 rounded border px-3 py-2 text-sm ' + cls + ' ' + className} role={tone === 'fail' ? 'alert' : 'status'}>
      <span className="flex-1 min-w-0">{children}</span>
      {action}
    </div>
  )
}

/** In development only: the API owner is still building this endpoint. */
export function NotYet({ what = 'This data' }) {
  if (!import.meta.env.DEV) return null
  return <p className="text-xs text-ink-3 mt-2">{what} is not available from the API yet. It will appear once the endpoint ships.</p>
}

/* --------------------------------------------------------------- overlays */

function useEscape(open, onClose) {
  useEffect(() => {
    if (!open) return
    const onKey = (e) => e.key === 'Escape' && onClose?.()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
}

export function Modal({ open, onClose, title, children, footer, width = 'max-w-lg', bodyClass = 'p-5' }) {
  useEscape(open, onClose)
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
      <div className="absolute inset-0 bg-ink/50" onClick={onClose} />
      <div className={'relative bg-white rounded-lg shadow-xl w-full max-h-[90vh] flex flex-col ' + width}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-none">
          <h2 className="text-xl text-ink">{title}</h2>
          <button className="inline-flex h-9 w-9 items-center justify-center rounded text-ink-3 hover:text-ink hover:bg-surface" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className={'overflow-y-auto min-h-0 ' + bodyClass}>{children}</div>
        {footer && <div className="px-5 py-4 border-t border-border flex justify-end gap-2 flex-none">{footer}</div>}
      </div>
    </div>
  )
}

/** Side panel. `side` left|right. Escape and the backdrop close it. */
export function Drawer({ open, onClose, title, children, side = 'right', width = 'w-[360px] max-w-[92vw]', footer }) {
  useEscape(open, onClose)
  const first = useRef(null)
  useEffect(() => { if (open) first.current?.focus() }, [open])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
      <div className="absolute inset-0 bg-ink/50" onClick={onClose} />
      <div className={'absolute inset-y-0 bg-white shadow-xl flex flex-col ' + width + ' ' + (side === 'left' ? 'left-0' : 'right-0')}>
        {title !== undefined && (
          <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-none">
            <h2 className="text-xl text-ink">{title}</h2>
            <button ref={first} className="inline-flex h-9 w-9 items-center justify-center rounded text-ink-3 hover:text-ink hover:bg-surface" onClick={onClose} aria-label="Close">✕</button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto min-h-0">{children}</div>
        {footer && <div className="px-5 py-4 border-t border-border flex justify-end gap-2 flex-none">{footer}</div>}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ toast */

let toastFn = null
export function toast(msg, kind = 'ok') { toastFn?.(msg, kind) }
export function Toaster() {
  const [items, setItems] = useState([])
  useEffect(() => {
    toastFn = (msg, kind) => {
      const id = Math.random()
      setItems((x) => [...x, { id, msg, kind }])
      setTimeout(() => setItems((x) => x.filter((i) => i.id !== id)), kind === 'error' ? 7000 : 3500)
    }
    return () => { toastFn = null }
  }, [])
  return (
    <div className="fixed bottom-4 right-4 z-[60] space-y-2 max-w-sm" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={'rounded-lg px-4 py-3 text-sm shadow-lg border ' +
          (t.kind === 'error' ? 'bg-status-deniedBg text-status-deniedTxt border-status-deniedTxt/20' : 'bg-ink text-white border-ink')}>
          {t.msg}
        </div>
      ))}
    </div>
  )
}

export function Copy({ text, label = 'Copy', className = '' }) {
  const [done, setDone] = useState(false)
  return (
    <button type="button" className={'btn-ghost px-3 py-1.5 text-xs ' + className}
      onClick={async () => { try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500) } catch { /* ignore */ } }}>
      {done ? 'Copied' : label}
    </button>
  )
}

export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="flex gap-1 border-b border-border overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist">
      {tabs.map((t) => (
        <button key={t.key} role="tab" aria-selected={value === t.key} onClick={() => onChange(t.key)}
          className={'px-4 py-2.5 min-h-[44px] text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ink-2 ' +
            (value === t.key ? 'border-red text-ink' : 'border-transparent text-ink-3 hover:text-ink')}>
          {t.label}{t.badge ? <span className="ml-1.5 badge badge-renewal">{t.badge}</span> : null}{t.dot && <span className={'ml-1.5 inline-block h-2 w-2 rounded-full align-middle ' + t.dot} aria-hidden />}
        </button>
      ))}
    </div>
  )
}

export function Spinner({ label = 'Loading…' }) {
  return <div className="text-sm text-ink-3 py-10 text-center animate-pulse" role="status">{label}</div>
}

/* ------------------------------------------------------------- health UI */

export const HEALTH_DOT = { ok: 'bg-status-approvedTxt', warn: 'bg-status-pendingTxt', fail: 'bg-status-deniedTxt', unknown: 'bg-ink-4', skip: 'bg-ink-4' }
export const HEALTH_LABEL = { ok: 'Healthy', warn: 'Needs attention', fail: 'Broken', unknown: 'Not checked yet', skip: 'Skipped' }

/** ok / warn / fail / skip icon used by health check lists. */
export function StatusIcon({ status, className = '' }) {
  const s = {
    ok: ['bg-status-approvedBg text-status-approvedTxt', '✓'],
    warn: ['bg-status-pendingBg text-status-pendingTxt', '!'],
    fail: ['bg-status-deniedBg text-status-deniedTxt', '✕'],
    skip: ['bg-surface text-ink-4', '–'],
  }[status] ?? ['bg-surface text-ink-4', '?']
  return <span className={'inline-flex h-5 w-5 flex-none items-center justify-center rounded-full text-[11px] font-bold ' + s[0] + ' ' + className} aria-label={status}>{s[1]}</span>
}
